import type {CodeNavigationInfo, DefinitionOrReference} from '@github-ui/code-nav'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {WebWorker} from '@github-ui/code-view-shared/utilities/web-worker'
import {DebouncedWorkerManager} from '@github-ui/code-view-shared/worker-jobs/debounced-worker-manager'
import {createFindInFileWorker} from '@github-ui/find-in-file-worker-job/create-find-in-file-worker'
import {type FindInFileRequest, type FindInFileResponse, findInFileWorkerJob} from '@github-ui/find-in-file-worker-job'
import React, {useEffect, useEffectEvent, useRef, useState} from 'react'

interface FindInFileWorkerRequest extends FindInFileRequest {
  currentCodeReferences: DefinitionOrReference[] | undefined
}

export type SearchInFileStatus = 'pending' | 'done'

export function useInFileSearchResults(
  codeNavInfo: CodeNavigationInfo | undefined,
  searchTerm: string,
  enabled = true,
) {
  const [searchResults, setSearchResults] = useState<DefinitionOrReference[]>([])
  const [focusedSearchResult, setFocusedSearchResult] = useState<number | undefined>(undefined)
  const [searchStatus, setSearchStatus] = useState<SearchInFileStatus>('done')
  const [previouslyEnabled, setPreviouslyEnabled] = useState(enabled)
  if (previouslyEnabled !== enabled) {
    setPreviouslyEnabled(enabled)
    if (!enabled) {
      setSearchResults([])
      setFocusedSearchResult(undefined)
      setSearchStatus('done')
    }
  }
  const workerManagerRef = React.useRef<DebouncedWorkerManager<FindInFileWorkerRequest, FindInFileResponse> | null>(
    null,
  )
  const path = usePath()
  const refInfo = useRefInfo()

  // eslint-disable-next-line react-hooks/refs
  if (enabled && !workerManagerRef.current && searchTerm) {
    workerManagerRef.current = new DebouncedWorkerManager<FindInFileWorkerRequest, FindInFileResponse>(
      new WebWorker(createFindInFileWorker, findInFileWorkerJob),
      200, // Disable debounce, since we do incremental searching anyway so it's ultra fast
      (req: FindInFileRequest) => req.query.length !== 1,
    )
  }

  // Search term ref is used in the worker `onResponse` callback to avoid changing callback on every search term change.
  const searchTermRef = useRef(searchTerm)
  // eslint-disable-next-line react-hooks/refs
  searchTermRef.current = searchTerm
  const lastSearchTerm = useRef('')

  useEffect(() => {
    return function destroy() {
      workerManagerRef.current?.terminate()
    }
  }, [])

  const lastCodeNavInfo = useRef<CodeNavigationInfo | undefined>(undefined)
  // A host switching search backends must release this worker and its cached query.
  useEffect(() => {
    if (enabled) return
    workerManagerRef.current?.terminate()
    workerManagerRef.current = null
    lastSearchTerm.current = ''
    lastCodeNavInfo.current = undefined
  }, [enabled])
  // eslint-disable-next-line react-hooks/refs
  if (workerManagerRef.current && codeNavInfo !== lastCodeNavInfo.current) {
    // eslint-disable-next-line react-hooks/refs
    workerManagerRef.current.onResponse = (data: FindInFileResponse) => {
      if (data.query === searchTermRef.current) {
        // TODO: Try to retain the focused search result (if possible) even as results change
        setFocusedSearchResult(0)
        setSearchResults(codeNavInfo?.createReferences(data.ranges) || [])
        setSearchStatus('done')
        lastSearchTerm.current = searchTermRef.current
      }
    }

    // eslint-disable-next-line react-hooks/refs
    lastCodeNavInfo.current = codeNavInfo
  }

  const onRefOrPathChange = useEffectEvent(() => {
    if (
      !enabled ||
      !codeNavInfo ||
      !workerManagerRef.current ||
      !lastSearchTerm.current ||
      lastSearchTerm.current === ''
    ) {
      //reset everything because we changed refs
      setSearchResults([])
      setFocusedSearchResult(0)
      setSearchStatus('done')
      return
    }
    setSearchResults([])
    setFocusedSearchResult(0)
    setSearchStatus('pending')
    workerManagerRef.current.post({
      query: lastSearchTerm.current,
      lines: codeNavInfo.blobLines,
      currentCodeReferences: undefined,
    })
  })

  //this effect re-searches for the previous search term when the ref changes - it wipes out all saved progress because
  //anything could have changed durring the swap between refs
  useEffect(() => {
    // We only want to trigger an update when the ref changes, aka the user is on a new branch or commit, or when
    //they navigate to a new file (path). If they are navigating using the symbols navigation, we want to redo the
    //search for that symbol within the new file as well
    onRefOrPathChange()
  }, [refInfo.name, path])

  const onSearchTermChange = useEffectEvent(() => {
    if (!enabled || !codeNavInfo || !workerManagerRef.current) {
      return
    }
    if (searchTerm === '') {
      setSearchResults([])
      setFocusedSearchResult(0)
      setSearchStatus('done')
      lastSearchTerm.current = ''
    } else if (lastSearchTerm.current === searchTerm || !validSearchTerm(searchTerm)) {
      return
    } else {
      setSearchStatus('pending')
      const canDoIncrementalSearch = lastSearchTerm.current.length > 0 && searchTerm.startsWith(lastSearchTerm.current)
      workerManagerRef.current.post({
        query: searchTerm,
        lines: codeNavInfo.blobLines,
        currentCodeReferences: canDoIncrementalSearch ? searchResults : undefined,
      })
    }
  })

  useEffect(() => {
    // We only want to trigger an update when the search term changes
    // eslint-disable-next-line react-hooks/set-state-in-effect
    onSearchTermChange()
  }, [searchTerm, enabled])

  return {
    focusedSearchResult,
    setFocusedSearchResult,
    searchResults,
    setSearchResults,
    searchStatus,
  }
}

function validSearchTerm(searchTerm: string) {
  return searchTerm.length > 0 && searchTerm.length <= 1000
}
